import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, Platform, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { Modal } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import RangePicker from '@components/Global/DateRangePicker/RangePicker';
import { AntDesign, MaterialIcons, Feather } from '@expo/vector-icons';
import { YStack, XStack, SizableText, Checkbox } from 'tamagui';
import { DataFilterOptions, resetFilters, hasActiveFilters } from '../utils/dataFilters';
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from 'expo-linear-gradient';

interface DataBrowserFiltersProps {
  visible: boolean;
  onClose: () => void;
  onApplyFilters: (filters: DataFilterOptions) => void;
  currentFilters: DataFilterOptions;
  availableTags?: string[];
  availableUsers?: Array<{ _id: string; label: string; email?: string }>;
}

const DataBrowserFilters: React.FC<DataBrowserFiltersProps> = ({
  visible,
  onClose,
  onApplyFilters,
  currentFilters,
  availableTags = [],
  availableUsers = [],
}) => {
  const theme = themeSelector();
  const [localFilters, setLocalFilters] = useState<DataFilterOptions>(currentFilters);
  const screenHeight = Dimensions.get('window').height;
  const maxModalHeight = screenHeight * 0.85; // 85% of screen height
  const minModalHeight = Math.min(400, screenHeight * 0.5); // Minimum 400px or 50% of screen

  // Update local filters when currentFilters prop changes
  React.useEffect(() => {
    setLocalFilters(currentFilters);
  }, [currentFilters, visible]);

  const handleDateRangeChange = useCallback(
    (dateRange: { startDate: Date; endDate: Date }) => {
      setLocalFilters((prev) => ({
        ...prev,
        dateRange: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        },
      }));
    },
    []
  );

  const handleAssignedToChange = useCallback((userId: string | null) => {
    setLocalFilters((prev) => ({
      ...prev,
      assignedTo: userId === prev.assignedTo ? null : userId,
    }));
  }, []);

  const handleFlaggedChange = useCallback((flagged: boolean | null) => {
    setLocalFilters((prev) => ({
      ...prev,
      flagged: prev.flagged === flagged ? null : flagged,
    }));
  }, []);

  const handleEnrichmentStatusChange = useCallback((status: string | null) => {
    setLocalFilters((prev) => ({
      ...prev,
      enrichmentStatus: prev.enrichmentStatus === status ? null : status,
    }));
  }, []);

  const handleTagToggle = useCallback((tag: string) => {
    setLocalFilters((prev) => {
      const currentTags = prev.tags || [];
      const newTags = currentTags.includes(tag)
        ? currentTags.filter((t) => t !== tag)
        : [...currentTags, tag];
      return {
        ...prev,
        tags: newTags,
      };
    });
  }, []);

  const handleApply = useCallback(() => {
    onApplyFilters(localFilters);
    onClose();
  }, [localFilters, onApplyFilters, onClose]);

  const handleReset = useCallback(() => {
    const reset = resetFilters();
    setLocalFilters(reset);
    onApplyFilters(reset);
    onClose();
  }, [onApplyFilters, onClose]);

  const activeFiltersCount = hasActiveFilters(localFilters)
    ? Object.values(localFilters).filter((v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === 'object' && 'startDate' in v) {
          return v.startDate !== null || v.endDate !== null;
        }
        if (Array.isArray(v)) return v.length > 0;
        return true;
      }).length
    : 0;

  const gradientColors = theme
    ? ['rgba(200, 162, 200, 0.15)', 'rgba(135, 206, 235, 0.15)']
    : ['rgba(200, 162, 200, 0.25)', 'rgba(135, 206, 235, 0.25)'];

  return (
    <Modal
      setVisible={onClose}
      isVisible={visible}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      animationInTiming={300}
      animationOutTiming={200}
      backdropOpacity={0.5}
      backdropColor="#000"
      backdropTransitionInTiming={300}
      backdropTransitionOutTiming={200}
      hasBackdrop={true}
      avoidKeyboard={true}
      coverScreen={true}
      useNativeDriver={true}
      useNativeDriverForBackdrop={true}
      hideModalContentWhileAnimating={true}
      propagateSwipe={false}
      onBackButtonPress={onClose}
      onBackdropPress={onClose}
      onSwipeComplete={onClose}
      style={styles.modal}
    >
      <View style={[styles.modalContainer, { 
        backgroundColor: theme ? '#ffffff' : '#1e293b',
        maxHeight: maxModalHeight,
        minHeight: minModalHeight,
        height: Platform.OS === 'web' ? 'auto' : undefined
      }]}>
        {/* Gradient Background */}
        <LinearGradient
          colors={gradientColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.gradientBackground}
        />

        {/* Header */}
        <XStack
          style={styles.header}
          alignItems="center"
          justifyContent="space-between"
          paddingHorizontal="$4"
          paddingVertical="$3"
        >
          <XStack alignItems="center" gap="$3">
            <MaterialIcons
              name="filter-list"
              size={24}
              color={theme ? '#3B82F6' : '#60A5FA'}
            />
            <SizableText
              size="$6"
              fontWeight="700"
              color={theme ? '#1e293b' : '#f8fafc'}
            >
              Filter Options
            </SizableText>
            {activeFiltersCount > 0 && (
              <View
                style={[
                  styles.badge,
                  { backgroundColor: theme ? '#EF4444' : '#DC2626' },
                ]}
              >
                <Text style={styles.badgeText}>{activeFiltersCount}</Text>
              </View>
            )}
          </XStack>
          <Pressable onPress={onClose} style={styles.closeButton}>
            <AntDesign
              name="close"
              size={20}
              color={theme ? '#64748b' : '#94a3b8'}
            />
          </Pressable>
        </XStack>

        {/* Content */}
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={true}
          contentContainerStyle={styles.contentContainer}
          nestedScrollEnabled={true}
        >
          {/* Date Range Filter */}
          <YStack gap="$2" marginBottom="$4">
            <SizableText
              size="$4"
              fontWeight="600"
              color={theme ? '#1e293b' : '#f8fafc'}
              marginBottom="$2"
            >
              Date Range
            </SizableText>
            <RangePicker
              startDate={localFilters.dateRange?.startDate || null}
              endDate={localFilters.dateRange?.endDate || null}
              dateCallback={handleDateRangeChange}
              noIcons={false}
            />
          </YStack>

          {/* Assigned To Filter */}
          {availableUsers.length > 0 && (
            <YStack gap="$2" marginBottom="$4">
              <SizableText
                size="$4"
                fontWeight="600"
                color={theme ? '#1e293b' : '#f8fafc'}
                marginBottom="$2"
              >
                Assigned To
              </SizableText>
              <YStack gap="$2">
                <Pressable
                  onPress={() => handleAssignedToChange('')}
                  style={[
                    styles.filterOption,
                    {
                      backgroundColor:
                        localFilters.assignedTo === ''
                          ? theme
                            ? '#dbeafe'
                            : '#1e3a8a'
                          : theme
                          ? '#f1f5f9'
                          : '#334155',
                      borderColor:
                        localFilters.assignedTo === ''
                          ? theme
                            ? '#3b82f6'
                            : '#60a5fa'
                          : theme
                          ? '#e2e8f0'
                          : '#475569',
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.filterOptionText,
                      {
                        color:
                          localFilters.assignedTo === ''
                            ? theme
                              ? '#1e40af'
                              : '#93c5fd'
                            : theme
                            ? '#1e293b'
                            : '#f8fafc',
                      },
                    ]}
                  >
                    Unassigned
                  </Text>
                  {localFilters.assignedTo === '' && (
                    <MaterialIcons
                      name="check-circle"
                      size={20}
                      color={theme ? '#3b82f6' : '#60a5fa'}
                    />
                  )}
                </Pressable>
                {availableUsers.map((user) => (
                  <Pressable
                    key={user._id}
                    onPress={() => handleAssignedToChange(user._id)}
                    style={[
                      styles.filterOption,
                      {
                        backgroundColor:
                          localFilters.assignedTo === user._id
                            ? theme
                              ? '#dbeafe'
                              : '#1e3a8a'
                            : theme
                            ? '#f1f5f9'
                            : '#334155',
                        borderColor:
                          localFilters.assignedTo === user._id
                            ? theme
                              ? '#3b82f6'
                              : '#60a5fa'
                            : theme
                            ? '#e2e8f0'
                            : '#475569',
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.filterOptionText,
                        {
                          color:
                            localFilters.assignedTo === user._id
                              ? theme
                                ? '#1e40af'
                                : '#93c5fd'
                              : theme
                              ? '#1e293b'
                              : '#f8fafc',
                        },
                      ]}
                    >
                      {user.label}
                    </Text>
                    {localFilters.assignedTo === user._id && (
                      <MaterialIcons
                        name="check-circle"
                        size={20}
                        color={theme ? '#3b82f6' : '#60a5fa'}
                      />
                    )}
                  </Pressable>
                ))}
              </YStack>
            </YStack>
          )}

          {/* Flagged Filter */}
          <YStack gap="$2" marginBottom="$4">
            <SizableText
              size="$4"
              fontWeight="600"
              color={theme ? '#1e293b' : '#f8fafc'}
              marginBottom="$2"
            >
              Flagged Status
            </SizableText>
            <XStack gap="$3">
              <Pressable
                onPress={() => handleFlaggedChange(true)}
                style={[
                  styles.filterChip,
                  {
                    backgroundColor:
                      localFilters.flagged === true
                        ? theme
                          ? '#fee2e2'
                          : '#7f1d1d'
                        : theme
                        ? '#f1f5f9'
                        : '#334155',
                    borderColor:
                      localFilters.flagged === true
                        ? theme
                          ? '#ef4444'
                          : '#fca5a5'
                        : theme
                        ? '#e2e8f0'
                        : '#475569',
                  },
                ]}
              >
                <Text
                  style={[
                    styles.filterChipText,
                    {
                      color:
                        localFilters.flagged === true
                          ? theme
                            ? '#dc2626'
                            : '#fca5a5'
                          : theme
                          ? '#64748b'
                          : '#94a3b8',
                    },
                  ]}
                >
                  Flagged
                </Text>
                {localFilters.flagged === true && (
                  <AntDesign
                    name="check"
                    size={16}
                    color={theme ? '#dc2626' : '#fca5a5'}
                  />
                )}
              </Pressable>
              <Pressable
                onPress={() => handleFlaggedChange(false)}
                style={[
                  styles.filterChip,
                  {
                    backgroundColor:
                      localFilters.flagged === false
                        ? theme
                          ? '#f0fdf4'
                          : '#14532d'
                        : theme
                        ? '#f1f5f9'
                        : '#334155',
                    borderColor:
                      localFilters.flagged === false
                        ? theme
                          ? '#10b981'
                          : '#34d399'
                        : theme
                        ? '#e2e8f0'
                        : '#475569',
                  },
                ]}
              >
                <Text
                  style={[
                    styles.filterChipText,
                    {
                      color:
                        localFilters.flagged === false
                          ? theme
                            ? '#059669'
                            : '#34d399'
                          : theme
                          ? '#64748b'
                          : '#94a3b8',
                    },
                  ]}
                >
                  Not Flagged
                </Text>
                {localFilters.flagged === false && (
                  <AntDesign
                    name="check"
                    size={16}
                    color={theme ? '#059669' : '#34d399'}
                  />
                )}
              </Pressable>
            </XStack>
          </YStack>

          {/* Enrichment Status Filter */}
          <YStack gap="$2" marginBottom="$4">
            <SizableText
              size="$4"
              fontWeight="600"
              color={theme ? '#1e293b' : '#f8fafc'}
              marginBottom="$2"
            >
              Enrichment Status
            </SizableText>
            <XStack gap="$2" flexWrap="wrap">
              {['completed', 'processing', 'failed'].map((status) => (
                <Pressable
                  key={status}
                  onPress={() =>
                    handleEnrichmentStatusChange(
                      localFilters.enrichmentStatus === status ? null : status
                    )
                  }
                  style={[
                    styles.filterChip,
                    {
                      backgroundColor:
                        localFilters.enrichmentStatus === status
                          ? theme
                            ? status === 'completed'
                              ? '#d1fae5'
                              : status === 'processing'
                              ? '#fef3c7'
                              : '#fee2e2'
                            : status === 'completed'
                            ? '#064e3b'
                            : status === 'processing'
                            ? '#78350f'
                            : '#7f1d1d'
                          : theme
                          ? '#f1f5f9'
                          : '#334155',
                      borderColor:
                        localFilters.enrichmentStatus === status
                          ? theme
                            ? status === 'completed'
                              ? '#10b981'
                              : status === 'processing'
                              ? '#f59e0b'
                              : '#ef4444'
                            : status === 'completed'
                            ? '#34d399'
                            : status === 'processing'
                            ? '#fbbf24'
                            : '#fca5a5'
                          : theme
                          ? '#e2e8f0'
                          : '#475569',
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      {
                        color:
                          localFilters.enrichmentStatus === status
                            ? theme
                              ? status === 'completed'
                                ? '#059669'
                                : status === 'processing'
                                ? '#d97706'
                                : '#dc2626'
                              : status === 'completed'
                              ? '#34d399'
                              : status === 'processing'
                              ? '#fbbf24'
                              : '#fca5a5'
                            : theme
                            ? '#64748b'
                            : '#94a3b8',
                        textTransform: 'capitalize',
                      },
                    ]}
                  >
                    {status}
                  </Text>
                  {localFilters.enrichmentStatus === status && (
                    <AntDesign
                      name="check"
                      size={16}
                      color={
                        theme
                          ? status === 'completed'
                            ? '#059669'
                            : status === 'processing'
                            ? '#d97706'
                            : '#dc2626'
                          : status === 'completed'
                          ? '#34d399'
                          : status === 'processing'
                          ? '#fbbf24'
                          : '#fca5a5'
                      }
                    />
                  )}
                </Pressable>
              ))}
            </XStack>
          </YStack>

          {/* Tags Filter */}
          {availableTags.length > 0 && (
            <YStack gap="$2" marginBottom="$4">
              <SizableText
                size="$4"
                fontWeight="600"
                color={theme ? '#1e293b' : '#f8fafc'}
                marginBottom="$2"
              >
                Tags
              </SizableText>
              <XStack gap="$2" flexWrap="wrap">
                {availableTags.map((tag) => {
                  const isSelected = localFilters.tags?.includes(tag) || false;
                  return (
                    <Pressable
                      key={tag}
                      onPress={() => handleTagToggle(tag)}
                      style={[
                        styles.filterChip,
                        {
                          backgroundColor: isSelected
                            ? theme
                              ? '#dbeafe'
                              : '#1e3a8a'
                            : theme
                            ? '#f1f5f9'
                            : '#334155',
                          borderColor: isSelected
                            ? theme
                              ? '#3b82f6'
                              : '#60a5fa'
                            : theme
                            ? '#e2e8f0'
                            : '#475569',
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.filterChipText,
                          {
                            color: isSelected
                              ? theme
                                ? '#1e40af'
                                : '#93c5fd'
                              : theme
                              ? '#1e293b'
                              : '#f8fafc',
                          },
                        ]}
                      >
                        {tag}
                      </Text>
                      {isSelected && (
                        <AntDesign
                          name="check"
                          size={16}
                          color={theme ? '#3b82f6' : '#60a5fa'}
                        />
                      )}
                    </Pressable>
                  );
                })}
              </XStack>
            </YStack>
          )}
        </ScrollView>

        {/* Footer Actions */}
        <XStack
          style={[styles.footer, { borderTopColor: theme ? '#e2e8f0' : '#374151' }]}
          gap="$3"
          paddingHorizontal="$4"
          paddingVertical="$3"
        >
          <Pressable
            onPress={handleReset}
            style={[
              styles.actionButton,
              styles.resetButton,
              {
                backgroundColor: theme ? '#f1f5f9' : '#334155',
                borderColor: theme ? '#e2e8f0' : '#475569',
              },
            ]}
          >
            <Text
              style={[
                styles.actionButtonText,
                { color: theme ? '#64748b' : '#94a3b8' },
              ]}
            >
              Reset
            </Text>
          </Pressable>
          <Pressable
            onPress={handleApply}
            style={[
              styles.actionButton,
              styles.applyButton,
              {
                backgroundColor: theme ? '#3b82f6' : '#1e40af',
              },
            ]}
          >
            <Text style={[styles.actionButtonText, { color: 'white' }]}>
              Apply Filters
            </Text>
          </Pressable>
        </XStack>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modal: {
    margin: 0,
    justifyContent: 'flex-end',
  },
  modalContainer: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
    flexDirection: 'column',
    width: '100%',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
      maxHeight: '85vh',
      minHeight: '400px',
    }),
  },
  gradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
    zIndex: 1,
    flexShrink: 0,
  },
  badge: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  closeButton: {
    padding: 4,
    borderRadius: 8,
  },
  content: {
    flex: 1,
    zIndex: 1,
    maxHeight: Platform.OS === 'web' ? 'calc(85vh - 140px)' : undefined,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 20,
    flexGrow: 1,
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 8,
  },
  filterOptionText: {
    fontSize: 14,
    fontWeight: '500',
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  footer: {
    zIndex: 1,
    backgroundColor: 'transparent',
    flexShrink: 0,
    borderTopWidth: 1,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  resetButton: {},
  applyButton: {},
  actionButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
});

export default DataBrowserFilters;

